import React, { useCallback } from "react";
import { useEffect, useState } from "react";
import { Card, Clickable } from "@warp-ds/react";
import IconChevronLeft24 from "@warp-ds/icons/react/chevron-left-24";
import IconChevronRight24 from "@warp-ds/icons/react/chevron-right-24";
import createHeartComponent from "@finn-no/favorite-heart-component";
import IconLandscape48 from "@warp-ds/icons/react/landscape-48";
import type { ExternalProps, RecommendationResponse } from "src/types.js";
import { useExternalProps } from "./components/useExternalProps.js";
import { i18n } from "@lingui/core";
import pulseSdk from "@finn-no/pulse-sdk";
import "@finn-no/favorite-heart-component/dist/style.css";
const { trackEvent } = pulseSdk;

import { messages as da } from "./locales/da.mjs";
import { messages as en } from "./locales/en.mjs";
import { messages as fi } from "./locales/fi.mjs";
import { messages as nb } from "./locales/nb.mjs";
import { TrackingContextFields } from "../models/MatchAdSearchResponse.js";

i18n.load({ da, en, nb, fi });

export const App = (props: { externalProps?: ExternalProps }) => {
	const externalProps = useExternalProps(props.externalProps);

	const locale = externalProps?.locale ?? "nb";

	i18n.activate(locale);

	const [showBackButton, setShowBackButton] = useState(false);
	const [showForwardButton, setShowForwardButton] = useState(true);
	const [recommendations, setRecommendations] = useState<RecommendationResponse>();
	const [recommendationsIsFetched, setRecommendationsIsFetched] = useState(false);
	const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);

	const filteredRecommendations = recommendations?.items.filter(({ price }) => !!price) || [];
	const isTouchDevice = window?.matchMedia("(pointer: coarse)").matches;

	const handleScroll = useCallback((container: HTMLDivElement) => {
		const isNearStart = container.scrollLeft > 25;
		const isNearEnd = container.scrollWidth - container.offsetWidth - container.scrollLeft > 25;

		setShowBackButton(isNearStart);
		setShowForwardButton(isNearEnd);
	}, []);

	const scrollContainerRef = useCallback(
		(node: HTMLDivElement | null) => {
			if (node) {
				setScrollContainer(node); // Store the node in state for later use
				node.addEventListener("scroll", () => handleScroll(node));
				handleScroll(node); // Initialize button states on mount
			}
		},
		[handleScroll],
	);

	useEffect(() => {
		if (recommendationsIsFetched) {
			return;
		}

		if (externalProps?.subvertical && externalProps.proxyUrl) {
			const url = `${externalProps?.proxyUrl}/recommendations?subvertical=${externalProps?.subvertical}`;
			fetch(url)
				.then((res) => res.json())
				.then((data: RecommendationResponse) => {
					setRecommendationsIsFetched(true);
					const trackingContexts = Object.entries(data["tracking-context"] ?? {});
					const recommendations: RecommendationResponse = {
						items: data.items.map((item) => {
							return {
								...item,
								tracking: {
									...item.tracking,
									pulse: item.tracking?.pulse
										? {
												...item.tracking.pulse,
												recommendation: item.tracking.pulse?.recommendation.map((event) => ({
													...event,
													...trackingContexts.reduce(
														(acc, [key, value]) => {
															acc[key] = value;
															return acc;
														},
														{} as Record<string, TrackingContextFields>,
													),
												})),
											}
										: undefined,
								},
							};
						}),
						"tracking-context": data["tracking-context"],
					};
					setRecommendations(recommendations);
				});
		}
	}, [externalProps?.subvertical, recommendationsIsFetched]);

	useEffect(() => {
		const shadowRoot = document.querySelector("aurora-mobility-recommendations-podlet-isolated")?.shadowRoot;
		if (!shadowRoot) {
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						trackVisibility(entry);
					}
				});
			},
			{
				root: null,
				threshold: 0.5,
			},
		);

		const elements = shadowRoot.querySelectorAll("[data-item-id]");
		elements.forEach((el) => observer.observe(el));

		return () => {
			elements.forEach((el) => observer.unobserve(el));
		};
	}, [filteredRecommendations]);

	const trackVisibility = (entry: IntersectionObserverEntry) => {
		const target = entry.target as HTMLElement;
		const itemId = target.dataset.itemId;

		const viewEvent = filteredRecommendations
			?.find((item) => item?.itemId === Number(itemId))
			?.tracking?.pulse?.recommendation?.find((event) => event?.type === "View");
		if (viewEvent) {
			trackEvent(viewEvent);
		}
	};

	const scrollList = (direction: "forward" | "backward") => {
		if (scrollContainer) {
			const scrollAmount = scrollContainer.offsetWidth + 12;
			const currentScroll = scrollContainer.scrollLeft;
			const newScrollPosition = direction === "forward" ? currentScroll + scrollAmount : currentScroll - scrollAmount;

			scrollContainer.scrollTo({ left: newScrollPosition, behavior: "smooth" });
		}
	};

	const Heart = createHeartComponent({
		baseUri: externalProps?.favoriteBaseUrl ?? "",
		userId: externalProps?.loginId ?? "",
		modalAttach: document.querySelector("aurora-mobility-recommendations-podlet-isolated")!
			.shadowRoot as unknown as HTMLElement,
	});

	if (!filteredRecommendations?.length || !externalProps) {
		return null;
	}

	return (
		<section className="flex flex-col gap-16 relative max-w-[1010]">
			<h3>
				{i18n.t({
					id: "mobility-recommendations-title",
					message: "Recommendations for you",
				})}
			</h3>
			<div
				className="flex gap-16 overflow-x-auto overflow-y-hidden rounded-4 -mr-16 md:mr-0 pl-2"
				ref={scrollContainerRef}
				style={{ scrollbarWidth: "none" }}
			>
				{filteredRecommendations.map((recommendation) => {
					const grayBg = `rgba(244, 244, 245, 1)`;
					const gradient =
						"linear-gradient(0deg, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.77) 15.625%, rgba(95, 95, 95, 0.49) 34.895%, rgba(196, 196, 196, 0.00) 50%, rgba(196, 196, 196, 0.00) 100%)";

					const noBgStyle = `${gradient}, url(${IconLandscape48}) center center / auto no-repeat, ${grayBg}`;
					const bgStyle = `${gradient}, url(${recommendation.imageUrl}) center center / cover no-repeat`;

					const resolvedBgStyle = recommendation.imageUrl ? bgStyle : noBgStyle;

					return (
						<div
							key={recommendation.itemId}
							data-item-id={recommendation.itemId}
							className="flex flex-col gap-16 transition-transform hover:scale-101 focus-within:scale-101 flex-shrink-0 w-[304] pt-2"
						>
							<Card className="min-w-[304] min-h-[224] rounded-8 border s-border">
								<article
									className="flex items-end p-16 relative h-full s-text-inverted font-bold"
									style={{
										background: resolvedBgStyle,
									}}
								>
									<Clickable
										href={`/${recommendation.itemId}`}
										title={recommendation.srText}
										aria-description={recommendation.srText}
										target="__blank"
										className="s-text-inverted font-bold"
										onClick={() => {
											const clickEvent = recommendation.tracking?.pulse?.recommendation?.find(
												({ type }) => type === "Click",
											);

											if (clickEvent) {
												trackEvent(clickEvent);
											}
										}}
									>
										<span>{recommendation.price}</span>
									</Clickable>
									<Heart
										isMobile={isTouchDevice}
										variant="heart-on-image"
										itemId={Number(recommendation.itemId)}
										itemType="Ad"
										// @ts-expect-error can be removed when a version with this pr is used:
										// https://github.schibsted.io/finn/favorite-heart-component/pull/73
										locale={locale}
										buttonClass="absolute top-0 right-0"
									/>
								</article>
							</Card>
							<div className="flex flex-col gap-8">
								<label className="font-bold truncate">{recommendation.title}</label>
								<span className="text-caption s-text-subtle">{recommendation.metadata}</span>
							</div>
						</div>
					);
				})}
			</div>
			{!isTouchDevice && showBackButton && (
				<button
					className="absolute -left-24 top-[160] transform -translate-y-1/2 s-bg-inverted rounded-full w-48 h-48 flex items-center justify-center shadow-m"
					onClick={() => scrollList("backward")}
				>
					<IconChevronLeft24 className="s-icon-inverted" />
				</button>
			)}
			{!isTouchDevice && showForwardButton && filteredRecommendations.length > 3 && (
				<button
					className="absolute -right-24 top-[160] transform -translate-y-1/2 s-bg-inverted rounded-full w-48 h-48 flex items-center justify-center shadow-m"
					onClick={() => scrollList("forward")}
				>
					<IconChevronRight24 className="s-icon-inverted" />
				</button>
			)}
		</section>
	);
};
